/**
 * Premium Shipping Protection manager (AU).
 *
 * Same setup as CA's assets/shipping-protection.js -- one shared manager that
 * owns the PSP line item, reconciles it against the cart through cart/update.js
 * and exposes updateProtection() for assets/tmg-cart-drawer.js to call after an
 * add, a quantity change or a remove. Ported so PSP is managed site-wide rather
 * than only by the cart page.
 *
 * What is AU's rather than CA's:
 *   - productId and the 100 tier variants
 *   - the tier/charge bands, including 2% of subtotal above $14,000
 *   - the subtotal basis (cart.total_price minus PSP, so cart-level discounts
 *     count), where CA sums line_price
 *   - the 'psp_enabled_v1' storage key, shared with the cart page so a toggle in
 *     one place is seen in the other
 *   - AUD formatting
 *   - pickup suppression: with Pickup selected there is nothing to protect
 */

(() => {
class ShippingProtection {
  constructor() {
    this.productId = 10197153743167;
    this.storageKey = 'psp_enabled_v1';
    this.protectionEnabled = this.getProtectionState();
    this.reconcilePromise = null;
    this.cartCache = null;
    this.cacheTimeout = 2000;
    this.variantIds = [
      51698394661183, 51698394693951, 51698394726719, 51698394759487, 51698394792255,
      51698394825023, 51698394857791, 51698394890559, 51698394923327, 51698394956095,
      51698394988863, 51698395021631, 51698395054399, 51698395087167, 51698395119935,
      51698395152703, 51698395185471, 51698395218239, 51698395251007, 51698395283775,
      51698395316543, 51698395349311, 51698395382079, 51698395414847, 51698395447615,
      51698395480383, 51698395513151, 51698395545919, 51698395578687, 51698395611455,
      51698395644223, 51698395676991, 51698395709759, 51698395742527, 51698395775295,
      51698395808063, 51698395840831, 51698395873599, 51698395906367, 51698395939135,
      51698395971903, 51698396004671, 51698396037439, 51698396070207, 51698396102975,
      51698396135743, 51698396168511, 51698396201279, 51698396234047, 51698396266815,
      51698396299583, 51698396332351, 51698396365119, 51698396397887, 51698396430655,
      51698396463423, 51698396496191, 51698396528959, 51698396561727, 51698396594495,
      51698396627263, 51698396660031, 51698396692799, 51698396725567, 51698396758335,
      51698396791103, 51698396823871, 51698396856639, 51698396889407, 51698396922175,
      51698396954943, 51698396987711, 51698397020479, 51698397053247, 51698397086015,
      51698397118783, 51698397151551, 51698397184319, 51698397217087, 51698397249855,
      51698397282623, 51698397315391, 51698397348159, 51698397380927, 51698397413695,
      51698397446463, 51698397479231, 51698397511999, 51698397544767, 51698397577535,
      51698397610303, 51698397643071, 51698397675839, 51698397708607, 51698397741375,
      51698397774143, 51698397806911, 51698397839679, 51698397872447, 51698397905215
    ];

    this.init();
  }

  init() {
    /* Cart page only, as on CA -- that is where the toggle and the PSP panel
       live. Off the cart page the manager still exists and is driven by
       assets/tmg-cart-drawer.js through updateProtection(). Locale-prefixed
       paths are allowed. */
    if (!/^(\/[a-z]{2}(-[a-z]{2})?)?\/cart\/?$/i.test(window.location.pathname)) {
      return;
    }

    const initializeCartPage = () => {
      this.showProtectionSection();
      this.setupCheckboxHandler();
      this.updateProtectionDisplay();
      this.autoAddProtectionOnLoad();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeCartPage, { once: true });
    } else {
      initializeCartPage();
    }
  }

  /* Pickup hides the panel entirely; sections/static-cart.liquid handles that
     when the selector changes, so only reveal it for a shipping cart. */
  showProtectionSection() {
    const section = document.getElementById('shipping-protection-section');
    if (section && !this.isPickupSelected()) {
      section.style.display = 'block';
    }
  }

  async autoAddProtectionOnLoad() {
    const result = await this.reconcileProtection();
    if (!result.ok || !result.changed) return;
    try {
      await this.refreshDisplay(result.cart);
    } catch (error) {
      this.showError(true);
    }
  }

  getCartUrl(endpoint) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${endpoint}`.replace(/\/{2,}/g, '/');
  }

  async getCartData(useCache = true) {
    if (useCache && this.cartCache && (Date.now() - this.cartCache.timestamp) < this.cacheTimeout) {
      return this.cartCache.data;
    }

    const response = await fetch(this.getCartUrl('cart.js'), {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    });
    if (!response.ok) {
      throw new Error(`Unable to read cart (${response.status})`);
    }
    const cart = await response.json();
    this.cartCache = { data: cart, timestamp: Date.now() };
    return cart;
  }

  getProtectionItems(cart) {
    return (cart.items || []).filter(item => Number(item.product_id) === this.productId);
  }

  getProtectionTotal(cart) {
    return this.getProtectionItems(cart).reduce((sum, item) => sum + (item.final_line_price ?? item.line_price ?? 0), 0);
  }

  /* AU's basis: the cart total less PSP, so cart-level discounts are reflected.
     CA instead sums line_price, which is before those discounts. */
  getCartSubtotalFromCart(cart) {
    return Math.max(0, (cart.total_price || 0) - this.getProtectionTotal(cart));
  }

  /* Pickup means no carrier leg, so there is nothing to protect. The selector
     only exists on the cart page; everywhere else this is false and the
     behaviour matches CA. */
  isPickupSelected() {
    return !!document.getElementById('method-pickup')?.checked;
  }

  /* AU's bands, in dollars. Identical to computePSPFromSubtotalDollars() in
     sections/main-cart-items.liquid -- keep the two in step. */
  calculateTierAndCharge(subtotalDollars) {
    let tier = 1;
    let charge = 30;

    if (subtotalDollars <= 0) {
      tier = 1;
      charge = 30;
    } else if (subtotalDollars <= 10000) {
      tier = Math.ceil(subtotalDollars / 1000);
      charge = tier * 30;
      if (charge > 300) charge = 300;
    } else if (subtotalDollars <= 14000) {
      tier = 10 + Math.ceil((subtotalDollars - 10000) / 1000);
      charge = 300;
    } else {
      tier = 14 + Math.ceil((subtotalDollars - 14000) / 1000);
      charge = Math.round((subtotalDollars * 0.02) / 10) * 10;
      if (charge < 300) charge = 300;
    }

    return { tier, chargeDollars: charge };
  }

  calculateRequiredTier(subtotalCents) {
    return this.calculateTierAndCharge(Math.round((subtotalCents || 0) / 100)).tier;
  }

  getVariantIdForTier(tier) {
    const idx = Math.max(0, tier - 1);
    return this.variantIds[idx] || this.variantIds[this.variantIds.length - 1];
  }

  getProtectionState() {
    try {
      const v = localStorage.getItem(this.storageKey);
      return v === null ? true : v === 'true';
    } catch (e) {
      return true;
    }
  }

  setProtectionState(enabled) {
    this.protectionEnabled = enabled;
    try {
      localStorage.setItem(this.storageKey, enabled ? 'true' : 'false');
    } catch (e) {}
  }

  getDesiredProtection(cart) {
    this.protectionEnabled = this.getProtectionState();
    const subtotal = this.getCartSubtotalFromCart(cart);
    const hasOtherItems = (cart.items || []).some(item => Number(item.product_id) !== this.productId);

    if (!this.protectionEnabled || !hasOtherItems || subtotal <= 0 || this.isPickupSelected()) {
      return { tier: 0, chargeDollars: 0, variantId: null };
    }

    const { tier, chargeDollars } = this.calculateTierAndCharge(Math.round(subtotal / 100));
    return { tier, chargeDollars, variantId: this.getVariantIdForTier(tier) };
  }

  isProtectionCorrect(cart, desiredVariantId) {
    const protectionItems = this.getProtectionItems(cart);
    if (desiredVariantId === null) {
      return protectionItems.length === 0;
    }
    return protectionItems.length === 1 &&
      Number(protectionItems[0].variant_id) === desiredVariantId &&
      protectionItems[0].quantity === 1;
  }

  buildProtectionUpdates(cart, desiredVariantId) {
    const updates = {};
    let keptDesiredItem = false;

    this.getProtectionItems(cart).forEach(item => {
      const isDesiredItem = desiredVariantId !== null &&
        Number(item.variant_id) === desiredVariantId &&
        !keptDesiredItem;
      updates[item.key] = isDesiredItem ? 1 : 0;
      if (isDesiredItem) keptDesiredItem = true;
    });

    // update.js sets an existing variant to exactly 1, or adds it at 1 if absent.
    // Unlike add.js, repeated or concurrent calls don't increment the quantity.
    if (desiredVariantId !== null && !keptDesiredItem) {
      updates[String(desiredVariantId)] = 1;
    }

    return updates;
  }

  async updateCartQuantities(updates) {
    const response = await fetch(this.getCartUrl('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates })
    });
    if (!response.ok) {
      throw new Error(`Unable to reconcile shipping protection (${response.status})`);
    }
    const cart = await response.json();
    this.cartCache = { data: cart, timestamp: Date.now() };
    return cart;
  }

  async reconcileProtectionInternal() {
    let cart = await this.getCartData(false);
    let changed = false;
    const initialProtectionCount = this.getProtectionItems(cart).length;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      const desired = this.getDesiredProtection(cart);
      if (this.isProtectionCorrect(cart, desired.variantId)) {
        return { ok: true, changed, initialProtectionCount, cart };
      }
      const updates = this.buildProtectionUpdates(cart, desired.variantId);
      cart = await this.updateCartQuantities(updates);
      changed = true;
    }

    const desired = this.getDesiredProtection(cart);
    return {
      ok: this.isProtectionCorrect(cart, desired.variantId),
      changed,
      initialProtectionCount,
      cart
    };
  }

  setBusy(busy) {
    this.busy = busy;
    document.dispatchEvent(new Event('shipping-protection:state'));
  }

  showError(visible) {
    const error = document.getElementById('shipping-protection-error');
    if (error) error.hidden = !visible;
  }

  async reconcileProtection() {
    if (this.reconcilePromise) {
      return this.reconcilePromise;
    }

    this.setBusy(true);
    this.reconcilePromise = this.reconcileProtectionInternal().catch(error => {
      console.error('Shipping protection safeguard failed:', error);
      return { ok: false, changed: false, initialProtectionCount: 0 };
    });

    try {
      return await this.reconcilePromise;
    } finally {
      this.reconcilePromise = null;
      this.setBusy(false);
    }
  }

  // Called by assets/tmg-cart-drawer.js after add / quantity / remove.
  async updateProtection() {
    return this.reconcileProtection();
  }

  async ensureProtectionForCheckout() {
    if (this.togglePromise) await this.togglePromise;
    if (this.reconcilePromise) await this.reconcilePromise;
    return (await this.reconcileProtection()).ok;
  }

  setupCheckboxHandler() {
    const checkbox = document.getElementById('shipping-protection-toggle');
    if (!checkbox) return;
    checkbox.checked = this.protectionEnabled;
    checkbox.addEventListener('change', (event) => {
      this.showError(false);
      this.setProtectionState(event.target.checked);
      this.reconcileProtection().then(result => {
        this.showError(!result.ok);
        return this.refreshDisplay(result.cart);
      }).catch(() => this.showError(true));
    });
  }

  async refreshDisplay(cart) {
    cart = cart || await this.getCartData(false);
    await this.updateProtectionDisplay(cart);
    if (typeof window.refreshCartPricingUI === 'function') {
      await window.refreshCartPricingUI(cart);
    }
  }

  async updateProtectionDisplay(cart) {
    cart = cart || await this.getCartData();
    const desired = this.getDesiredProtection(cart);

    const priceDisplay = document.getElementById('protection-price-display');
    if (priceDisplay) {
      const costLabel = priceDisplay.dataset.costLabel;
      const amount = this.formatMoney(desired.chargeDollars * 100);
      priceDisplay.textContent = costLabel ? `${costLabel} ${amount}` : amount;
    }

    const pspAmount = document.getElementById('psp-amount');
    if (pspAmount) {
      pspAmount.textContent = this.formatMoney(this.getProtectionTotal(cart));
    }
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' })
        .format((cents || 0) / 100);
    } catch (e) {
      return '$' + ((cents || 0) / 100).toFixed(2) + ' AUD';
    }
  }
}

if (!window.shippingProtection) {
  window.shippingProtection = new ShippingProtection();
}
})();
